/**
 * 月間pt 明細モーダル
 * 月間pt カードのクリックで気泡式に展開し、対象月の実績pt 子チケット明細を表示する。
 * 当月は完了 + 処理中、過去月は完了のみ（処理中pt は現在スナップショットのため対象外）。
 * チケットキーは Backlog の該当票へリンクする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 対象月の開発pt 明細モーダル（当月は完了子チケット + 処理中子チケット、過去月は完了子チケットのみ）。Backlog 票へのリンク付き
 * @graph-connects useMemberDevelopPoints [calls] 完了pt 明細フック
 * @graph-connects useMemberWipPoints [calls] 処理中pt 明細フック（当月のみ有効化）
 */
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { useMemberDevelopPoints, useMemberWipPoints } from '../../hooks/useMember.js';
import { formatNum } from './combat-power/combat-power-utils.js';
import { IssueLink, PtDetailTable, SectionSpinner, TD_CLASS, TH_CLASS } from './pt-detail.js';
import { monthEndOf } from '../../lib/fiscal-quarter.js';

/** @graph-connects none */
interface CurrentMonthPtDetailModalProps {
  /** メンバーのメールアドレス */
  email: string;
  /** 対象月の月初日 (YYYY-MM-01) */
  month: string;
  /**
   * 対象月が当月（JST）か。処理中pt は「現在スナップショット」のため当月のみ意味を持つ。
   * 過去月では処理中セクションを表示せず、合計にも含めない（完了分のみ）。
   */
  isCurrentMonth: boolean;
  /** モーダル表示状態 */
  open: boolean;
  /** クローズ要求コールバック */
  onClose: () => void;
}

/** @graph-connects none */
const SECTION_TITLE_CLASS = 'text-sm font-bold text-[var(--color-text-primary)]';

/** @graph-connects useMemberDevelopPoints [calls] 完了pt 明細フック / useMemberWipPoints [calls] 処理中pt 明細フック */
export function CurrentMonthPtDetailModal({
  email,
  month,
  isCurrentMonth,
  open,
  onClose,
}: CurrentMonthPtDetailModalProps) {
  const { t } = useTranslation();

  // open のときだけ取得（モーダルを開くまで遅延）
  const { data: completedData, isLoading: isCompletedLoading } = useMemberDevelopPoints(
    open ? email : null,
    open ? { startDate: month, endDate: monthEndOf(month), limit: 500 } : null,
  );
  // 処理中pt は現在スナップショットのため当月のみ取得・表示する（過去月では意味を持たない）
  const { data: wipData, isLoading: isWipLoading } = useMemberWipPoints(
    email,
    open && isCurrentMonth,
  );

  const completedTotal = (completedData?.data ?? []).reduce((s, d) => s + d.developPoint, 0);
  const wipTotal = isCurrentMonth ? (wipData?.totalPoint ?? 0) : 0;
  const monthTitle = `${String(parseInt(month.slice(0, 4), 10))}/${String(parseInt(month.slice(5, 7), 10))}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('members.quarterlyKpi.ptDetail.title', { month: monthTitle })}
      description={t(
        isCurrentMonth
          ? 'members.quarterlyKpi.ptDetail.description'
          : 'members.quarterlyKpi.ptDetail.descriptionCompleted',
        {
          value: formatNum(completedTotal + wipTotal, 1),
        },
      )}
      size="full"
      className="animate-bubble-in md:max-w-7xl"
    >
      <div className="space-y-6">
        {/* 完了 セクション（種別・日付列を持つため専用テーブル） */}
        <section>
          <h4 className={SECTION_TITLE_CLASS}>
            {t('members.quarterlyKpi.ptDetail.completedSection', {
              value: formatNum(completedTotal, 1),
            })}
          </h4>
          {isCompletedLoading ? (
            <SectionSpinner />
          ) : (completedData?.data.length ?? 0) > 0 ? (
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className={TH_CLASS}>{t('members.quarterlyKpi.ptDetail.colIssue')}</th>
                    <th className={`${TH_CLASS} w-full`}>
                      {t('members.quarterlyKpi.ptDetail.colSummary')}
                    </th>
                    <th className={TH_CLASS}>{t('members.quarterlyKpi.ptDetail.colType')}</th>
                    <th className={TH_CLASS}>{t('members.quarterlyKpi.ptDetail.colDate')}</th>
                    <th className={`${TH_CLASS} text-right`}>pt</th>
                  </tr>
                </thead>
                <tbody>
                  {(completedData?.data ?? []).map((d) => (
                    <tr
                      key={d.issueKey}
                      className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
                    >
                      <td className={TD_CLASS}>
                        <IssueLink issueKey={d.issueKey} />
                      </td>
                      <td className={TD_CLASS}>{d.summary}</td>
                      <td
                        className={`${TD_CLASS} text-xs text-[var(--color-text-secondary)] whitespace-nowrap`}
                      >
                        {d.issueTypeName}
                      </td>
                      <td
                        className={`${TD_CLASS} text-xs text-[var(--color-text-secondary)] whitespace-nowrap`}
                      >
                        {d.completedDate}
                      </td>
                      <td className={`${TD_CLASS} text-right font-medium whitespace-nowrap`}>
                        {formatNum(d.developPoint, 1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-2 text-sm text-[var(--color-text-muted)]">
              {t('members.quarterlyKpi.ptDetail.empty')}
            </p>
          )}
        </section>

        {/* 処理中 セクション（当月のみ。過去月は現在スナップショットが無意味なため非表示） */}
        {isCurrentMonth && (
          <section>
            <h4 className={SECTION_TITLE_CLASS}>
              {t('members.quarterlyKpi.ptDetail.wipSection', {
                value: formatNum(wipData?.totalPoint ?? 0, 1),
              })}
            </h4>
            {isWipLoading ? (
              <SectionSpinner />
            ) : (wipData?.data.length ?? 0) > 0 ? (
              <PtDetailTable rows={wipData?.data ?? []} wrapperClassName="mt-2 overflow-x-auto" />
            ) : (
              <p className="mt-2 text-sm text-[var(--color-text-muted)]">
                {t('members.quarterlyKpi.ptDetail.empty')}
              </p>
            )}
          </section>
        )}

        {/* 注記: 当月は完了+処理中（現在スナップショット）、過去月は完了分のみ */}
        <p className="text-xs text-[var(--color-text-muted)]">
          {t(
            isCurrentMonth
              ? 'members.quarterlyKpi.ptDetail.note'
              : 'members.quarterlyKpi.ptDetail.noteCompleted',
          )}
        </p>
      </div>
    </Modal>
  );
}
